export interface VermietungsAkte {
  mietbeginn: string | null;
  mietende: string | null;
  gesamtbetrag: number | null;
}

export interface HeatmapTag {
  datum: string;
  typ: 'frei' | 'vermietet';
}

function parseDatum(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysDiff(von: Date, bis: Date): number {
  const ms = startOfDay(bis).getTime() - startOfDay(von).getTime();
  return Math.round(ms / 86400000) + 1;
}

export function berechneAuslastungsQuote(tageVermietet: number, zeitraumTage: number): number {
  if (zeitraumTage === 0) return 0;
  const quote = (tageVermietet / zeitraumTage) * 100;
  return Math.round(Math.min(quote, 100) * 10) / 10;
}

export function berechneTageVermietet(akten: VermietungsAkte[], since: Date): number {
  const sinceDay = startOfDay(since);
  const today = startOfDay(new Date());
  const tageSet = new Set<string>();

  for (const akte of akten) {
    const beginn = parseDatum(akte.mietbeginn);
    if (!beginn) continue;
    const ende = parseDatum(akte.mietende) ?? today;
    let current = startOfDay(beginn) < sinceDay ? new Date(sinceDay) : startOfDay(beginn);
    const effectiveEnde = startOfDay(ende);
    while (current <= effectiveEnde) {
      tageSet.add(isoDate(current));
      current = addDays(current, 1);
    }
  }

  return tageSet.size;
}

export function berechneUmsatzYTD(akten: VermietungsAkte[], year: number): number {
  return akten.reduce((sum, akte) => {
    if (!akte.mietbeginn || akte.gesamtbetrag == null) return sum;
    const beginn = parseDatum(akte.mietbeginn);
    if (!beginn || beginn.getFullYear() !== year) return sum;
    return sum + akte.gesamtbetrag;
  }, 0);
}

export function berechneDurchschnittTagessatz(akten: VermietungsAkte[]): number {
  if (akten.length === 0) return 0;
  let totalBetrag = 0;
  let totalTage = 0;

  for (const akte of akten) {
    const beginn = parseDatum(akte.mietbeginn);
    const ende = parseDatum(akte.mietende);
    if (!beginn || !ende || akte.gesamtbetrag == null) continue;
    const tage = daysDiff(startOfDay(beginn), startOfDay(ende));
    totalBetrag += akte.gesamtbetrag;
    totalTage += tage;
  }

  if (totalTage === 0) return 0;
  return Math.round((totalBetrag / totalTage) * 100) / 100;
}

export function berechneHeatmapDaten(
  akten: VermietungsAkte[],
  vonIso: string,
  bisIso: string,
): HeatmapTag[] {
  const von = startOfDay(new Date(vonIso));
  const bis = startOfDay(new Date(bisIso));
  const result: HeatmapTag[] = [];

  let current = von;
  while (current <= bis) {
    const currentIso = isoDate(current);
    const typ = akten.some(akte => {
      const beginn = parseDatum(akte.mietbeginn);
      const ende = parseDatum(akte.mietende) ?? startOfDay(new Date());
      if (!beginn) return false;
      return startOfDay(beginn) <= current && current <= startOfDay(ende);
    }) ? 'vermietet' : 'frei';

    result.push({ datum: currentIso, typ });
    current = addDays(current, 1);
  }

  return result;
}

export function formatiereDateRange(vonIso: string, bisIso: string): string {
  const von = new Date(vonIso);
  const bis = new Date(bisIso);
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
  return `${von.toLocaleDateString('de-DE', opts)} – ${bis.toLocaleDateString('de-DE', opts)}`;
}
